import Alert from "@amzn/meridian/alert";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "src/components/shared/loadingSpinner/loadingSpinner";
import {
  FILE_TYPE_COLORS,
  FILE_TYPE_LABELS,
  fileTypeTotals,
  formatCount,
  formatReportTimestamp,
  latestRunAt,
  overviewTotals,
  regionShortLabel,
} from "./adminReportUtils";
import ChartLegend from "./components/chartLegend";
import FilesByRegionChart from "./components/filesByRegionChart";
import FileTypeDistributionChart from "./components/fileTypeDistributionChart";
import RegionalPerformanceTable from "./components/regionalPerformanceTable";
import ReportCard from "./components/reportCard";
import LoadError from "./components/loadError";
import ReportPageHeader from "./components/reportPageHeader";
import StatCards from "./components/statCards";
import adminReportService from "./service/adminReportService";
import { AdminReportOverview, RegionCode } from "./types";

const Overview = () => {
  const navigate = useNavigate();
  const [overview, setOverview] = useState<AdminReportOverview>();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const loadOverview = async () => {
    setIsLoading(true);
    setHasError(false);
    try {
      setOverview(await adminReportService.getOverview());
    } catch {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadOverview();
  }, []);

  const goToRegionDocuments = (region: RegionCode) =>
    navigate(`/admin/documents?region=${region}`);

  if (isLoading) {
    return (
      <div className="admin-report">
        <LoadingSpinner />
      </div>
    );
  }

  if (hasError || !overview) {
    return (
      <div className="admin-report">
        <div className="admin-report__body">
          <LoadError subject="the admin report" onRetry={loadOverview} />
        </div>
      </div>
    );
  }

  const { regions, unavailableRegions } = overview;
  const updatedAt = latestRunAt(regions);
  const totals = overviewTotals(regions);
  const typeTotals = fileTypeTotals(regions);

  const barLegend = typeTotals.map(({ fileType }) => ({
    label: FILE_TYPE_LABELS[fileType],
    color: FILE_TYPE_COLORS[fileType],
  }));

  const donutLegend = typeTotals.map(({ fileType, count }) => ({
    label: FILE_TYPE_LABELS[fileType],
    color: FILE_TYPE_COLORS[fileType],
    value: count,
  }));

  return (
    <div className="admin-report">
      <ReportPageHeader
        actions={
          updatedAt && (
            <span className="admin-report__updated">
              <span className="admin-report__live-dot" />
              {`Last Run On ${formatReportTimestamp(updatedAt)}`}
            </span>
          )
        }
      />

      <div className="admin-report__body">
        {unavailableRegions.length > 0 && (
          <Alert type="warning" size="small">
            {`Could not read ${unavailableRegions
              .map(regionShortLabel)
              .join(", ")}. The totals below cover the other regions only.`}
          </Alert>
        )}

        <StatCards
          totals={totals}
          // Accepted mismatch: the card totals all regions, the destination
          // list is scoped to one realm.
          onUncoveredClick={() =>
            navigate("/admin/documents?coverage=uncovered")
          }
          onFailedClick={() => navigate("/admin/failures")}
        />

        <div className="admin-report__overview-grid">
          <ReportCard title="Regional Performance" padded={false}>
            <RegionalPerformanceTable
              regions={regions}
              onRegionClick={goToRegionDocuments}
            />
          </ReportCard>

          <div className="admin-report__overview-side">
            <ReportCard
              title="Files by Region"
              headerActions={<ChartLegend entries={barLegend} />}
            >
              <FilesByRegionChart regions={regions} />
            </ReportCard>

            <ReportCard
              title="File Type Distribution"
              subtitle={`${formatCount(
                totals.totalDocuments,
              )} total files across all regions`}
            >
              <FileTypeDistributionChart totals={typeTotals} />
              <ChartLegend entries={donutLegend} />
            </ReportCard>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Overview;
